import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import verificationImage from '../../assets/images/services-2.png';
import tecnvironsLogo from '../../assets/images/Tecnvirons-logo.jpeg';
import './EmailVerificationModal.css';

const EmailVerificationModal = ({ isOpen, onClose, onVerifySuccess, userEmail, verificationContext }) => {
  const { verifyEmail, resendVerification } = useAuth();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const normalizedIdentifier = String(userEmail || '').trim();
  const isPhoneIdentifier = /^\+?\d{8,15}$/.test(normalizedIdentifier.replace(/\s+/g, ''));
  const resolvedAuthType = verificationContext?.authType || (isPhoneIdentifier ? 'phone' : 'email');
  const preferredPhoneMethod = verificationContext?.method || 'whatsapp';
  const headingText = isPhoneIdentifier ? 'Verify Your Phone Number' : 'Verify Your Email';
  const actionText = isPhoneIdentifier ? 'Verify Phone Number' : 'Verify Email';
  const targetHintText = isPhoneIdentifier ? 'phone number' : 'email';

  const maskEmail = (email) => {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (!domain) return email;
    return local.charAt(0) + '*'.repeat(Math.max(local.length - 1, 3)) + '@' + domain;
  };

  const maskPhone = (phone) => {
    if (!phone) return '';
    const s = phone.replace(/\s+/g, '');
    if (s.length <= 6) return s;
    return s.slice(0, 3) + '*'.repeat(s.length - 7) + s.slice(-4);
  };

  useEffect(() => {
    let timer;
    if (isOpen && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [countdown, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setOtp(['', '', '', '', '', '']);
      setError('');
      setSuccess('');
      setCanResend(false);
      setCountdown(60);
    }
  }, [isOpen]);

  const handleOtpChange = (index, value) => {
    const numericValue = String(value).replace(/\D/g, '');
    if (numericValue.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = numericValue;
    setOtp(newOtp);

    if (error) setError('');

    if (numericValue && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').split('');

    const newOtp = [...otp];
    for (let i = 0; i < Math.min(digits.length, 6); i++) {
      newOtp[i] = digits[i];
    }

    setOtp(newOtp);
    setError('');

    const nextEmptyIndex = newOtp.findIndex((val, idx) => !val && idx >= digits.length);
    const focusIndex = nextEmptyIndex >= 0 ? nextEmptyIndex : Math.min(digits.length, 5);
    const focusInput = document.getElementById(`otp-${focusIndex}`);
    if (focusInput) {
      setTimeout(() => focusInput.focus(), 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join('');

    if (otpValue.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    if (!userEmail) {
      setError(`${isPhoneIdentifier ? 'Phone number' : 'Email address'} is missing`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await verifyEmail(userEmail, otpValue, resolvedAuthType);

      if (result.success) {
        setSuccess(isPhoneIdentifier ? 'Phone number verified successfully!' : 'Email verified successfully!');
        setTimeout(() => {
          onVerifySuccess && onVerifySuccess(result.user);
        }, 1500);
      } else {
        setError(result.error);
        setOtp(['', '', '', '', '', '']);
        const firstInput = document.getElementById('otp-0');
        if (firstInput) firstInput.focus();
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend || isResending || !userEmail) return;

    setIsResending(true);
    setError('');

    try {
      const resendPayload = isPhoneIdentifier
        ? {
            identifier: userEmail,
            phone: userEmail,
            authType: 'phone',
            method: preferredPhoneMethod || 'whatsapp'
          }
        : {
            identifier: userEmail,
            email: userEmail,
            authType: 'email'
          };

      const result = await resendVerification(resendPayload);

      if (result.success) {
        setSuccess('Verification code sent successfully!');
        setCanResend(false);
        setCountdown(60);
        setOtp(['', '', '', '', '', '']);
        const firstInput = document.getElementById('otp-0');
        if (firstInput) firstInput.focus();

        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to resend verification code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="email-verification-modal-overlay" onClick={onClose}>
      <div
        className="email-verification-modal-container auth-modal"
        onClick={e => e.stopPropagation()}
        style={{ '--modal-bg-image': `url(${verificationImage})` }}
      >
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>

        <div className="modal-form-section">
          <div className="form-content" data-lenis-prevent>
            <div className="auth-modal-header">
              <img src={tecnvironsLogo} alt="Tecnvirons" className="modal-logo" />
            </div>

            <div className="form-main">
              <h2 className="form-title">{headingText}</h2>
              <p className="form-subtitle" style={{ marginTop: 0 }}>
                We've sent a 6-digit verification code to:<br />
                <strong>{isPhoneIdentifier ? maskPhone(normalizedIdentifier) : maskEmail(normalizedIdentifier)}</strong>
              </p>

              {success && <div className="success-message">{success}</div>}
              {error && <div className="error-message">{error}</div>}

              <form onSubmit={handleSubmit} className="otp-form">
                <div className="otp-inputs">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      className="otp-input"
                      disabled={isLoading}
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      aria-label={`OTP digit ${index + 1}`}
                    />
                  ))}
                </div>

                <div className="verification-actions">
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={isLoading || otp.join('').length !== 6}
                  >
                    {isLoading ? 'Verifying...' : actionText}
                  </button>

                  {canResend ? (
                    <button
                      type="button"
                      className="resend-btn"
                      onClick={handleResendOtp}
                      disabled={isResending}
                    >
                      {isResending ? 'Sending...' : 'Resend Code'}
                    </button>
                  ) : (
                    <button type="button" className="resend-btn" disabled>
                      Resend in {countdown}s
                    </button>
                  )}
                </div>
              </form>

              <div className="instructions">
                <h4>Having trouble?</h4>
                <ul>
                  <li>{isPhoneIdentifier ? 'Check your SMS/WhatsApp inbox' : 'Check your spam/junk folder'}</li>
                  <li>{`Make sure you entered the correct ${targetHintText}`}</li>
                  <li>The code expires in 10 minutes</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-image-section">
          <img src={verificationImage} alt={isPhoneIdentifier ? 'Phone Verification' : 'Email Verification'} className="modal-image" />
          <div className="image-overlay">
            <div className="decorative-shape top-left-shape"></div>
            <div className="decorative-shape bottom-right-shape"></div>

            <div className="overlay-content">
              <div className="overlay-card">
                <h3>Almost There!</h3>
                <p>Just one more step to unlock the full power of Tecnvirons.</p>
              </div>
              <div className="bottom-overlay">
                <h3>Secure Your Account,<br />Unlock Your Potential!</h3>
                <p>Verification ensures your account security.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationModal;
